import { useState } from "react";
import { Plus } from "lucide-react";
import {
  pickCategoryInput,
  useBuckets,
  useCategories,
  useSaveCategory,
  type Category,
} from "@/api/finance";
import { useLedgerAccounts } from "@/api/books";
import { CategoryForm } from "@/components/forms/CategoryForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

type Editing = { mode: "new" } | { mode: "edit"; category: Category } | null;

export function CategoriesPanel() {
  const ledgerAccounts = useLedgerAccounts(false);
  const expenseAccounts = (ledgerAccounts.data ?? []).filter((a) => a.kind === "expense");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const categories = useCategories(includeArchived);
  const buckets = useBuckets();
  const save = useSaveCategory();
  const nextSortOrder = Math.max(0, ...(categories.data ?? []).map((c) => c.sort_order)) + 1;

  return (
    <div className="flex flex-col gap-4">
      <Alert title="What kind of cost a bill is">
        Each category names the bucket a bill of that kind draws from, unless the bill says
        otherwise. These are the categories from the directors' finance tracker.
      </Alert>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id="categories-archived"
          label="Show archived categories"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })} disabled={!buckets.data}>
            <Plus className="h-4 w-4" aria-hidden />
            Add category
          </Button>
        )}
      </div>

      {editing && buckets.data && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New category" : `Edit ${editing.category.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryForm
              key={
                editing.mode === "edit"
                  ? `${editing.category.id}-${editing.category.version}`
                  : "new"
              }
              initial={editing.mode === "edit" ? editing.category : null}
              buckets={buckets.data}
              ledgerAccounts={expenseAccounts}
              nextSortOrder={nextSortOrder}
              submitLabel={editing.mode === "new" ? "Add category" : "Save category"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.category.id, version: editing.category.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void categories.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={save.error} onReload={() => void categories.refetch()} />

      {categories.isPending ? (
        <p className="text-muted-foreground">Loading categories…</p>
      ) : categories.isError ? (
        <RequestError error={categories.error} />
      ) : (
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Category</th>
                <th className="px-4 py-2 font-medium">Default bucket</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.data.map((category) => (
                <tr key={category.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <span className="font-medium">{category.name}</span>
                    {category.archived && (
                      <Badge variant="muted" className="ml-2">
                        Archived
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2">{category.default_bucket_name}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {!category.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ mode: "edit", category })}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={save.isPending}
                      onClick={() =>
                        save.mutate({
                          input: { ...pickCategoryInput(category), archived: !category.archived },
                          existing: { id: category.id, version: category.version },
                        })
                      }
                    >
                      {category.archived ? "Restore" : "Archive"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
